<div
  class="folder-column"
  [class.folder-column--drag-over]="isDragOver()"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)">
  <div class="folder-column__header">
    <span class="folder-column__header-name" [title]="headerLabel()">{{ headerLabel() }}</span>
    <span class="folder-column__header-count">{{ itemCount() }}</span>
    <span class="folder-column__header-actions">
      @if (isFolderColumn()) {
        <p-button
          icon="pi pi-upload"
          [text]="true"
          styleClass="folder-column__icon-btn"
          [disabled]="!canUpload()"
          [ariaLabel]="'DOCUMENT_MANAGEMENT.UPLOAD' | translate"
          [pTooltip]="uploadTooltipKey() | translate"
          tooltipPosition="top"
          (onClick)="onUploadClick()" />
      }
      <p-button
        icon="pi pi-folder-plus"
        [text]="true"
        styleClass="folder-column__icon-btn"
        [disabled]="!canCreateFolder()"
        [ariaLabel]="'DOCUMENT_MANAGEMENT.NEW_FOLDER' | translate"
        [pTooltip]="newFolderTooltipKey() | translate"
        tooltipPosition="top"
        (onClick)="onNewFolder()" />
    </span>
  </div>
  <input
    #fileInput
    type="file"
    multiple
    class="folder-column__file-input"
    aria-hidden="true"
    tabindex="-1"
    (change)="onFileInputChange($event)" />
  <div class="folder-column__body">
    @if (hasFailures()) {
      <ul class="folder-column__failures" role="alert">
        @for (failure of uploadFailures(); track failure.id) {
          <li class="folder-column__failure">
            <i class="pi pi-exclamation-circle folder-column__failure-icon" aria-hidden="true"></i>
            <span class="folder-column__failure-text">
              <span class="folder-column__failure-file">{{ failure.fileName }}</span>
              <span class="folder-column__failure-message">{{ failure.message }}</span>
            </span>
            <p-button
              icon="pi pi-times"
              [text]="true"
              size="small"
              styleClass="folder-column__failure-dismiss"
              [ariaLabel]="'GENERAL.DISMISS' | translate"
              (onClick)="dismissFailure(failure.id)" />
          </li>
        }
      </ul>
    }

    @if (isLoading() && !hasAnyContent()) {
      <div class="folder-column__loading">
        <p-progressSpinner styleClass="folder-column__spinner" strokeWidth="3" />
      </div>
    } @else if (hasError() && !hasAnyContent()) {
      <div class="folder-column__error">
        <span>{{ 'DOCUMENT_MANAGEMENT.LOAD_ERROR' | translate }}</span>
        <p-button
          icon="pi pi-refresh"
          [text]="true"
          size="small"
          [label]="'GENERAL.RETRY' | translate"
          (onClick)="reload()" />
      </div>
    } @else if (!hasAnyContent()) {
      <div class="folder-column__empty">
        <span>{{ 'DOCUMENT_MANAGEMENT.EMPTY_FOLDER' | translate }}</span>
        @if (canUpload()) {
          <span class="folder-column__empty-hint">
            {{ 'DOCUMENT_MANAGEMENT.DROP_FILES_HINT' | translate }}
          </span>
        }
      </div>
    } @else {
      @if (showSmartEntries()) {
        <ul
          class="folder-column__list folder-column__list--smart"
          role="listbox"
          [attr.aria-label]="'DOCUMENT_MANAGEMENT.SMART_LIST_ARIA_LABEL' | translate">
          <li
            class="folder-column__row folder-column__row--smart"
            [class.folder-column__row--selected]="selectedSmart() === 'recent'"
            role="option"
            tabindex="0"
            [attr.aria-selected]="selectedSmart() === 'recent'"
            (click)="onSelectSmart('recent')"
            (keydown)="onSelectSmartKeydown($event, 'recent')">
            <i class="pi pi-clock folder-column__icon" aria-hidden="true"></i>
            <span class="folder-column__name">{{ 'DOCUMENT_MANAGEMENT.RECENT' | translate }}</span>
            <i class="pi pi-chevron-right folder-column__chevron" aria-hidden="true"></i>
          </li>
          <li
            class="folder-column__row folder-column__row--smart"
            [class.folder-column__row--selected]="selectedSmart() === 'starred'"
            role="option"
            tabindex="0"
            [attr.aria-selected]="selectedSmart() === 'starred'"
            (click)="onSelectSmart('starred')"
            (keydown)="onSelectSmartKeydown($event, 'starred')">
            <i class="pi pi-star folder-column__icon" aria-hidden="true"></i>
            <span class="folder-column__name">{{ 'DOCUMENT_MANAGEMENT.STARRED' | translate }}</span>
            <i class="pi pi-chevron-right folder-column__chevron" aria-hidden="true"></i>
          </li>
          <li
            class="folder-column__row folder-column__row--smart"
            [class.folder-column__row--selected]="selectedSmart() === 'deleted'"
            role="option"
            tabindex="0"
            [attr.aria-selected]="selectedSmart() === 'deleted'"
            (click)="onSelectSmart('deleted')"
            (keydown)="onSelectSmartKeydown($event, 'deleted')">
            <i class="pi pi-trash folder-column__icon" aria-hidden="true"></i>
            <span class="folder-column__name">{{ 'DOCUMENT_MANAGEMENT.DELETED' | translate }}</span>
            <i class="pi pi-chevron-right folder-column__chevron" aria-hidden="true"></i>
          </li>
        </ul>
      }

      @if (showSmartEntries() && hasFolders()) {
        <div class="folder-column__divider" aria-hidden="true"></div>
      }

      @if (hasFolders()) {
        <ul
          class="folder-column__list folder-column__list--folders"
          role="listbox"
          [attr.aria-label]="'DOCUMENT_MANAGEMENT.FOLDER_LIST_ARIA_LABEL' | translate">
          @for (folder of folders(); track folder.id) {
            <li
              class="folder-column__row folder-column__row--folder"
              [class.folder-column__row--selected]="folder.id === selectedChildId()"
              [class.folder-column__row--dragging]="folder.id === draggingRowId()"
              [class.folder-column__row--drop-target]="folder.id === dropHoverFolderId()"
              role="option"
              tabindex="0"
              [attr.aria-selected]="folder.id === selectedChildId()"
              [attr.draggable]="isFolderDraggable(folder) ? 'true' : 'false'"
              (click)="onSelectFolder(folder)"
              (keydown)="onSelectFolderKeydown($event, folder)"
              (dragstart)="onFolderRowDragStart($event, folder)"
              (dragend)="onRowDragEnd()"
              (dragover)="onFolderRowDragOver($event, folder)"
              (dragleave)="onFolderRowDragLeave($event, folder)"
              (drop)="onFolderRowDrop($event, folder)">
              <i
                class="pi folder-column__icon"
                [class.pi-folder]="folder.kind !== 'managed_branch'"
                [class.pi-server]="folder.kind === 'managed_branch'"
                aria-hidden="true"></i>
              <span class="folder-column__name">{{ folder.displayName ?? folder.name }}</span>
              <i class="pi pi-chevron-right folder-column__chevron" aria-hidden="true"></i>
              @if (showTags() && folder.tags.length > 0) {
                <tf-tag-editor
                  class="folder-column__row-tags"
                  [tags]="folder.tags"
                  [editable]="false" />
              }
            </li>
          }
        </ul>
      }

      @if (hasFolders() && (hasDocuments() || hasPending())) {
        <div class="folder-column__divider" aria-hidden="true"></div>
      }

      @if (hasPending()) {
        <h3 class="folder-column__section-heading">
          {{ 'DOCUMENT_MANAGEMENT.UPLOADING' | translate }}
        </h3>
        <ul
          class="folder-column__pending-list"
          [attr.aria-label]="'DOCUMENT_MANAGEMENT.UPLOADING' | translate">
          @for (pending of pendingUploads(); track pending.id) {
            <li
              class="folder-column__row folder-column__row--document folder-column__row--pending"
              aria-busy="true">
              <span
                class="folder-column__doc-thumb"
                [class]="mimeColorClassFor(pending.mimeType)"
                aria-hidden="true">
                {{ mimeShortLabel(pending.mimeType) }}
              </span>
              <span class="folder-column__doc-meta">
                <span class="folder-column__doc-name">{{ pending.fileName }}</span>
                <span class="folder-column__doc-sub">
                  {{ pendingUploadStatus(pending) }}
                </span>
                <span
                  class="folder-column__upload-progress"
                  [class.folder-column__upload-progress--indeterminate]="pending.progress === null"
                  [style.--folder-upload-progress]="pendingUploadProgressWidth(pending)"
                  role="progressbar"
                  [attr.aria-valuemin]="0"
                  [attr.aria-valuemax]="100"
                  [attr.aria-valuenow]="
                    pending.progress === null ? null : pendingUploadProgressValue(pending)
                  ">
                  <span class="folder-column__upload-progress-fill"></span>
                </span>
              </span>
            </li>
          }
        </ul>
      }

      @if (hasPending() && hasDocuments()) {
        <div class="folder-column__divider" aria-hidden="true"></div>
      }

      @if (hasDocuments()) {
        <ul
          class="folder-column__list folder-column__list--documents"
          role="listbox"
          [attr.aria-label]="'DOCUMENT_MANAGEMENT.DOCUMENT_LIST_ARIA_LABEL' | translate">
          @for (doc of documents(); track doc.id) {
            <li
              class="folder-column__row folder-column__row--document"
              [class.folder-column__row--selected]="doc.id === selectedDocumentId()"
              [class.folder-column__row--incomplete]="isIncomplete(doc)"
              [class.folder-column__row--dragging]="doc.id === draggingRowId()"
              role="option"
              tabindex="0"
              [attr.aria-selected]="doc.id === selectedDocumentId()"
              [attr.draggable]="isIncomplete(doc) ? 'false' : 'true'"
              (click)="onSelectDocument(doc)"
              (keydown)="onSelectDocumentKeydown($event, doc)"
              (dragstart)="onDocumentRowDragStart($event, doc)"
              (dragend)="onRowDragEnd()">
              <span
                class="folder-column__doc-thumb"
                [class]="mimeColorClassFor(doc.mimeType)"
                aria-hidden="true">
                {{ mimeShortLabel(doc.mimeType) }}
              </span>
              <span class="folder-column__doc-meta">
                <span class="folder-column__doc-name" [title]="doc.name">{{ doc.name }}</span>
                <span class="folder-column__doc-sub">
                  @if (isIncomplete(doc)) {
                    <span class="folder-column__incomplete-tag">
                      {{ 'DOCUMENT_MANAGEMENT.UPLOAD_INCOMPLETE' | translate }}
                    </span>
                  } @else if (doc.size !== undefined) {
                    {{ formatSize(doc.size) }}
                  }
                </span>
              </span>
              @if (isIncomplete(doc)) {
                <p-button
                  icon="pi pi-trash"
                  [text]="true"
                  size="small"
                  styleClass="folder-column__remove-btn"
                  [ariaLabel]="'DOCUMENT_MANAGEMENT.REMOVE_INCOMPLETE' | translate"
                  [pTooltip]="'DOCUMENT_MANAGEMENT.REMOVE_INCOMPLETE' | translate"
                  (onClick)="$event.stopPropagation(); onRemoveIncomplete(doc)" />
              }
              @if (showTags() && doc.tags.length > 0) {
                <tf-tag-editor
                  class="folder-column__row-tags"
                  [tags]="doc.tags"
                  [editable]="false" />
              }
            </li>
          }
        </ul>
      }
    }

    @if (dragOverMode(); as mode) {
      <div class="folder-column__drop-overlay" aria-hidden="true">
        <i
          class="pi folder-column__drop-icon"
          [class.pi-cloud-upload]="mode === 'upload'"
          [class.pi-folder]="mode === 'os-folder-create-root'"
          [class.pi-arrow-up]="mode === 'move-to-root'"></i>
        <span>
          @switch (mode) {
            @case ('move-to-root') {
              {{ 'DOCUMENT_MANAGEMENT.DROP_TO_MOVE_TO_ROOT' | translate }}
            }
            @case ('os-folder-create-root') {
              {{ 'DOCUMENT_MANAGEMENT.DROP_FOLDERS_HERE' | translate }}
            }
            @default {
              {{ 'DOCUMENT_MANAGEMENT.DROP_FILES_HERE' | translate }}
            }
          }
        </span>
      </div>
    }
  </div>
</div>

<p-dialog
  [visible]="conflictDialog() !== null"
  (visibleChange)="onConflictDialogVisibilityChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [header]="'DOCUMENT_MANAGEMENT.CONFLICT_TITLE' | translate"
  styleClass="folder-column-conflict-dialog">
  @if (conflictDialog(); as conflict) {
    <div class="folder-column-conflict-dialog__body">
      <p class="folder-column-conflict-dialog__message">
        {{
          'DOCUMENT_MANAGEMENT.CONFLICT_MESSAGE'
            | translate
              : {
                  name: conflict.existingName,
                  path: conflict.displayPath,
                }
        }}
      </p>
      <label
        class="folder-column-conflict-dialog__label"
        [for]="'folder-upload-conflict-rename-' + conflict.id">
        {{ 'DOCUMENT_MANAGEMENT.CONFLICT_RENAME_LABEL' | translate }}
      </label>
      <input
        #conflictInput
        pInputText
        class="folder-column-conflict-dialog__input"
        [id]="'folder-upload-conflict-rename-' + conflict.id"
        [value]="conflictRenameValue()"
        [attr.aria-invalid]="conflictRenameError() ? true : null"
        [attr.aria-describedby]="
          conflictRenameError() ? 'folder-upload-conflict-rename-error' : null
        "
        (input)="onConflictRenameInput($event)"
        (keydown.enter)="onConflictRename()" />
      @if (conflictRenameError()) {
        <small
          id="folder-upload-conflict-rename-error"
          class="folder-column-conflict-dialog__error">
          {{ conflictRenameError() }}
        </small>
      }
      @if (conflict.newVersionDisabledReason) {
        <small class="folder-column-conflict-dialog__hint">
          {{ conflict.newVersionDisabledReason }}
        </small>
      }
    </div>
  }
  <ng-template pTemplate="footer">
    <p-button
      [label]="'DOCUMENT_MANAGEMENT.CONFLICT_SKIP' | translate"
      severity="secondary"
      [text]="true"
      (onClick)="onConflictSkip()" />
    <p-button
      [label]="'DOCUMENT_MANAGEMENT.CONFLICT_NEW_VERSION' | translate"
      severity="secondary"
      [outlined]="true"
      [disabled]="!conflictDialog()?.canUploadNewVersion"
      (onClick)="onConflictNewVersion()" />
    <p-button
      [label]="'DOCUMENT_MANAGEMENT.CONFLICT_RENAME' | translate"
      severity="primary"
      (onClick)="onConflictRename()" />
  </ng-template>
</p-dialog>
